import React, { useEffect, useMemo, useState } from "react";
import client, { apiErr } from "../lib/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import { LineChart, TrendingUp, Play, Download, RefreshCw, Globe, MousePointerClick, Trophy, CheckCircle2, AlertTriangle, Sparkles } from "lucide-react";
import { LANGS } from "../i18n";

const APP_NAMES = {
  family_tree: "Familigi™", benefits: "SEBAA™", currency: "Currencida™",
  holdings: "BoxAlong™", suite: "Full Suite",
};
const LANG_NATIVE = Object.fromEntries(LANGS.map((l) => [l.code, l.native]));
const C = { completed: "#24a148", midway: "#f1c21b", fainting: "#da1e28", views: "#0f62fe", visitors: "#08bdba" };
const WINDOWS = [
  ["today", "Today"], ["h48", "48 hours"], ["h72", "72 hours"],
  ["d7", "7 days"], ["d14", "14 days"], ["d30", "30 days"],
];

function Stat({ label, value, sub, testid }) {
  return (
    <div className="rounded-lg border border-black/10 p-3 dark:border-white/10" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wide opacity-55">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
      {sub && <div className="text-xs opacity-55">{sub}</div>}
    </div>
  );
}

function BucketBar({ completed, midway, fainting }) {
  const total = completed + midway + fainting || 1;
  const seg = (v, color) => v > 0 ? (
    <div style={{ width: `${(100 * v) / total}%`, background: color }} className="h-full" />
  ) : null;
  return (
    <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-black/10 dark:bg-white/10">
      {seg(completed, C.completed)}{seg(midway, C.midway)}{seg(fainting, C.fainting)}
    </div>
  );
}

function TrafficTab() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const load = () => client.get("/admin/analytics").then((r) => setData(r.data)).catch((e) => setErr(apiErr(e)));
  useEffect(() => { load(); }, []);
  if (err) return <p className="text-sm text-[#da1e28]" data-testid="traffic-error">{err}</p>;
  if (!data) return <p className="text-sm opacity-60" data-testid="traffic-loading">Loading traffic…</p>;
  const s = data.summary;
  return (
    <div className="space-y-6" data-testid="fopseo-traffic">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="traffic-windows">
        {WINDOWS.map(([k, label]) => (
          <div key={k} className="rounded-lg border border-black/10 p-4 dark:border-white/10" data-testid={`traffic-window-${k}`}>
            <div className="text-xs font-semibold uppercase tracking-wide text-[var(--primary)]">{label}</div>
            <div className="mt-2 grid grid-cols-3 gap-2 text-center">
              <div><div className="text-xl font-semibold tabular-nums" data-testid={`traffic-${k}-views`}>{s[k].views}</div><div className="text-[10px] opacity-55">Views</div></div>
              <div><div className="text-xl font-semibold tabular-nums" data-testid={`traffic-${k}-visitors`}>{s[k].visitors}</div><div className="text-[10px] opacity-55">Visitors</div></div>
              <div><div className="text-xl font-semibold tabular-nums" data-testid={`traffic-${k}-downloads`}>{s[k].downloads}</div><div className="text-[10px] opacity-55">Downloads</div></div>
            </div>
          </div>
        ))}
      </div>

      <div>
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold"><TrendingUp size={16} /> 30-day trend</div>
        <div className="h-64 w-full rounded-lg border border-black/10 p-2 dark:border-white/10" data-testid="traffic-trend">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.daily} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
              <XAxis dataKey="day" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} interval={4} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="views" name="Views" fill={C.views} radius={[2, 2, 0, 0]} />
              <Bar dataKey="visitors" name="Visitors" fill={C.visitors} radius={[2, 2, 0, 0]} />
              <Bar dataKey="downloads" name="Downloads" fill={C.completed} radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div>
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold"><MousePointerClick size={16} /> Top pages (30 days)</div>
          <div className="overflow-hidden rounded-lg border border-black/10 dark:border-white/10" data-testid="top-pages">
            <table className="w-full text-sm">
              <thead className="bg-black/5 text-start text-xs uppercase tracking-wide opacity-60 dark:bg-white/5">
                <tr><th className="px-3 py-2">Page</th><th className="px-3 py-2 text-end">Views</th><th className="px-3 py-2 text-end">Visitors</th></tr>
              </thead>
              <tbody>
                {data.top_pages.length === 0 && <tr><td colSpan={3} className="px-3 py-4 text-center opacity-50">No page views recorded yet.</td></tr>}
                {data.top_pages.map((p) => (
                  <tr key={p.path} className="border-t border-black/5 dark:border-white/5" data-testid={`top-page-${p.path}`}>
                    <td className="px-3 py-2 font-mono text-xs">{p.path}</td>
                    <td className="px-3 py-2 text-end tabular-nums">{p.views}</td>
                    <td className="px-3 py-2 text-end tabular-nums">{p.visitors}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div>
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold"><Download size={16} /> Downloads (30 days)</div>
          <div className="overflow-hidden rounded-lg border border-black/10 dark:border-white/10" data-testid="downloads-by-ref">
            <table className="w-full text-sm">
              <thead className="bg-black/5 text-start text-xs uppercase tracking-wide opacity-60 dark:bg-white/5">
                <tr><th className="px-3 py-2">Asset</th><th className="px-3 py-2 text-end">Count</th></tr>
              </thead>
              <tbody>
                {data.downloads_by_ref.length === 0 && <tr><td colSpan={2} className="px-3 py-4 text-center opacity-50">No downloads recorded yet.</td></tr>}
                {data.downloads_by_ref.map((d) => (
                  <tr key={d.ref} className="border-t border-black/5 dark:border-white/5" data-testid={`download-${d.ref}`}>
                    <td className="px-3 py-2">{d.ref}</td>
                    <td className="px-3 py-2 text-end tabular-nums">{d.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function Heatmap({ demos, langs }) {
  const max = Math.max(1, ...demos.flatMap((d) => langs.map((l) => d.by_lang[l].plays)));
  const cell = (v) => {
    if (!v) return "transparent";
    const a = 0.12 + 0.78 * (v / max);
    return `rgba(15,98,254,${a.toFixed(3)})`;
  };
  return (
    <div className="overflow-x-auto rounded-lg border border-black/10 dark:border-white/10" data-testid="demo-heatmap">
      <table className="min-w-[760px] w-full text-xs">
        <thead>
          <tr className="text-start opacity-60">
            <th className="sticky left-0 bg-[var(--card,#fff)] px-2 py-2">Demo \ Language</th>
            {langs.map((l) => <th key={l} className="px-1 py-2 text-center" title={LANG_NATIVE[l] || l}>{l}</th>)}
          </tr>
        </thead>
        <tbody>
          {demos.map((d) => (
            <tr key={d.app} className="border-t border-black/5 dark:border-white/5">
              <td className="sticky left-0 bg-[var(--card,#fff)] px-2 py-1.5 font-medium">{APP_NAMES[d.app]}</td>
              {langs.map((l) => {
                const v = d.by_lang[l].plays;
                return <td key={l} className="px-1 py-1.5 text-center tabular-nums" style={{ background: cell(v) }} data-testid={`heat-${d.app}-${l}`}>{v || ""}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DemosTab() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [sel, setSel] = useState("family_tree");
  const [sort, setSort] = useState("date");
  useEffect(() => { client.get("/admin/demo-analytics").then((r) => setData(r.data)).catch((e) => setErr(apiErr(e))); }, []);
  const selected = useMemo(() => data?.demos.find((d) => d.app === sel), [data, sel]);
  const dailySorted = useMemo(() => {
    if (!selected) return [];
    const rows = [...selected.daily];
    if (sort === "date") return rows;
    return rows.sort((a, b) => (b[sort] || 0) - (a[sort] || 0));
  }, [selected, sort]);
  if (err) return <p className="text-sm text-[#da1e28]" data-testid="demos-error">{err}</p>;
  if (!data) return <p className="text-sm opacity-60" data-testid="demos-loading">Loading demo analytics…</p>;
  const overallRate = data.total_plays ? Math.round((100 * data.total_completed) / data.total_plays) : 0;
  const weekly = data.demos.map((d) => {
    const last7 = d.daily.slice(-7);
    return { app: d.app, plays7: last7.reduce((s, x) => s + x.plays, 0), completed7: last7.reduce((s, x) => s + x.completed, 0) };
  });
  const top2 = [...weekly].filter((w) => w.plays7 > 0).sort((a, b) => b.plays7 - a.plays7).slice(0, 2);
  return (
    <div className="space-y-6" data-testid="fopseo-demos">
      <div className="rounded-lg border border-amber-400/40 bg-amber-400/[0.08] p-4" data-testid="demo-top-week">
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold"><Trophy size={16} className="text-amber-500" /> Most-watched demos this week</div>
        {top2.length === 0 ? (
          <p className="text-xs opacity-55">No demo plays in the last 7 days yet.</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {top2.map((w, idx) => (
              <div key={w.app} className="flex items-center gap-3 rounded-md bg-white/60 p-3 dark:bg-black/20" data-testid={`demo-top-${idx + 1}`}>
                <div className="grid h-8 w-8 place-items-center rounded-full bg-amber-400 text-sm font-bold text-white">{idx + 1}</div>
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold">{APP_NAMES[w.app]}</div>
                  <div className="text-xs opacity-60">{w.plays7} plays · {w.completed7} reached 75%+ this week</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid gap-3 sm:grid-cols-3" data-testid="demo-overall">
        <Stat label="Total plays (30d)" value={data.total_plays} testid="demo-total-plays" />
        <Stat label="Reached 75%+" value={data.total_completed} testid="demo-total-completed" />
        <Stat label="Overall completion" value={`${overallRate}%`} sub="completed ÷ plays" testid="demo-overall-rate" />
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5" data-testid="demo-cards">
        {data.demos.map((d) => (
          <button key={d.app} onClick={() => setSel(d.app)}
            className={`rounded-lg border p-3 text-start transition ${sel === d.app ? "border-[var(--primary)] bg-[var(--primary)]/[0.05]" : "border-black/10 hover:bg-black/[0.03] dark:border-white/10"}`}
            data-testid={`demo-card-${d.app}`}>
            <div className="text-sm font-semibold">{APP_NAMES[d.app]}</div>
            <div className="mt-1 flex items-baseline gap-1"><span className="text-2xl font-semibold tabular-nums">{d.plays}</span><span className="text-[10px] opacity-55">plays</span></div>
            <div className="mt-2"><BucketBar completed={d.completed} midway={d.midway} fainting={d.fainting} /></div>
            <div className="mt-1 text-[11px] opacity-60">{d.completion_rate}% completed</div>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3 text-[11px]">
        <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-sm" style={{ background: C.completed }} /> Completed (75%+)</span>
        <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-sm" style={{ background: C.midway }} /> Midway (25–74%)</span>
        <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-sm" style={{ background: C.fainting }} /> Fainting starts (1–24%)</span>
      </div>

      <div>
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold"><Globe size={16} /> Plays by demo × language (30 days)</div>
        <Heatmap demos={data.demos} langs={data.langs} />
      </div>

      <div>
        <div className="mb-2 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-sm font-semibold"><Play size={16} /> Daily bars — {APP_NAMES[sel]}</div>
          <div className="ms-auto flex items-center gap-1 text-xs">
            <span className="opacity-55">Sort by</span>
            {[["date", "Date"], ["completed", "Completed"], ["midway", "Midway"], ["fainting", "Fainting"]].map(([k, lbl]) => (
              <button key={k} onClick={() => setSort(k)} data-testid={`demo-sort-${k}`}
                className={`rounded-md px-2 py-1 ${sort === k ? "bg-[var(--primary)] text-white" : "border border-black/15 dark:border-white/15"}`}>{lbl}</button>
            ))}
          </div>
        </div>
        <div className="h-64 w-full rounded-lg border border-black/10 p-2 dark:border-white/10" data-testid="demo-daily">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={dailySorted} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
              <XAxis dataKey="day" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} interval={sort === "date" ? 4 : 2} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="completed" stackId="a" name="Completed (75%+)" fill={C.completed} />
              <Bar dataKey="midway" stackId="a" name="Midway (25–74%)" fill={C.midway} />
              <Bar dataKey="fainting" stackId="a" name="Fainting (1–24%)" fill={C.fainting} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div>
        <div className="mb-2 text-sm font-semibold">{APP_NAMES[sel]} — by language</div>
        <div className="overflow-x-auto rounded-lg border border-black/10 dark:border-white/10" data-testid="demo-lang-table">
          <table className="w-full text-sm">
            <thead className="bg-black/5 text-start text-xs uppercase tracking-wide opacity-60 dark:bg-white/5">
              <tr><th className="px-3 py-2">Language</th><th className="px-3 py-2 text-end">Plays</th><th className="px-3 py-2 text-end">75%+</th><th className="px-3 py-2 text-end">Midway</th><th className="px-3 py-2 text-end">Fainting</th><th className="px-3 py-2 text-end">Rate</th></tr>
            </thead>
            <tbody>
              {selected && data.langs.filter((l) => selected.by_lang[l].plays > 0).map((l) => {
                const b = selected.by_lang[l];
                return (
                  <tr key={l} className="border-t border-black/5 dark:border-white/5" data-testid={`demo-lang-${l}`}>
                    <td className="px-3 py-2">{LANG_NATIVE[l] || l} <span className="opacity-40">({l})</span></td>
                    <td className="px-3 py-2 text-end tabular-nums">{b.plays}</td>
                    <td className="px-3 py-2 text-end tabular-nums text-[#24a148]">{b.completed}</td>
                    <td className="px-3 py-2 text-end tabular-nums text-[#b28600]">{b.midway}</td>
                    <td className="px-3 py-2 text-end tabular-nums text-[#da1e28]">{b.fainting}</td>
                    <td className="px-3 py-2 text-end tabular-nums">{b.completion_rate}%</td>
                  </tr>
                );
              })}
              {selected && data.langs.every((l) => selected.by_lang[l].plays === 0) && (
                <tr><td colSpan={6} className="px-3 py-4 text-center opacity-50">No plays recorded for this demo yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function covColor(cell) {
  if (!cell.in_scope) return "rgba(120,120,120,0.10)";
  if (cell.missing > 0) return "rgba(218,30,40,0.16)";        // structure missing → red
  if (cell.untranslated > 0) return "rgba(241,194,27,0.22)";   // present but some English → amber
  return "rgba(36,161,72,0.20)";                               // fully covered → green
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

function TranslationsTab() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [job, setJob] = useState(null); // {bundleId, lang, done, total, status}
  const [note, setNote] = useState("");
  const [bulk, setBulk] = useState(null); // {done, total} — overall "Fill everything" progress
  const load = () => client.get("/admin/translation-coverage").then((r) => setData(r.data)).catch((e) => setErr(apiErr(e)));
  useEffect(() => { load(); }, []);

  // Fill one language of one bundle; polls the background job to completion.
  const fillLang = async (bundleId, lang) => {
    setNote("");
    setJob({ bundleId, lang, done: 0, total: 0, status: "running" });
    try {
      const { data: started } = await client.post("/admin/translation-coverage/fill", { bundle_id: bundleId, lang });
      const jid = started.job_id;
      for (let i = 0; i < 600; i++) {
        await sleep(1500);
        const { data: s } = await client.get("/admin/translation-coverage/fill/status", { params: { job_id: jid } });
        setJob({ bundleId, lang, done: s.done, total: s.total, status: s.status });
        if (s.status === "done") { setNote(`Filled ${lang} — ${s.total} string(s) translated.`); break; }
        if (s.status === "error") { setNote(`Fill failed for ${lang}: ${s.error || "unknown error"}`); break; }
      }
    } catch (e) {
      setNote(apiErr(e));
    } finally {
      setJob(null);
      await load();
    }
  };

  // Sequentially fill every language of a bundle that has a real gap: a missing section, or a
  // language not yet added to the manual's scope. (Brand-name strings kept in English on purpose
  // are not treated as gaps; use a single click on an amber tile to force-retranslate one.)
  const bundleTargets = (bundle) =>
    bundle.coverage.filter((c) => c.code !== "ENG" && (c.missing > 0 || !c.in_scope));
  const fillBundleGaps = async (bundle) => {
    for (const c of bundleTargets(bundle)) {
      // eslint-disable-next-line no-await-in-loop
      await fillLang(bundle.id, c.code);
    }
  };

  // Queue every gap across every manual into one sequential run with overall progress.
  const fillEverything = async () => {
    const queue = [];
    data.bundles.forEach((b) => bundleTargets(b).forEach((c) => queue.push([b.id, c.code])));
    if (!queue.length) return;
    for (let i = 0; i < queue.length; i++) {
      setBulk({ done: i, total: queue.length });
      // eslint-disable-next-line no-await-in-loop
      await fillLang(queue[i][0], queue[i][1]);
    }
    setBulk(null);
    setNote(`Fill everything complete — ${queue.length} language(s) processed across all manuals.`);
  };

  const totalGaps = data ? data.bundles.reduce((s, b) => s + b.coverage.filter((c) => c.code !== "ENG" && (c.missing > 0 || !c.in_scope)).length, 0) : 0;

  if (err) return <p className="text-sm text-[#da1e28]" data-testid="translations-error">{err}</p>;
  if (!data) return <p className="text-sm opacity-60" data-testid="translations-loading">Loading translation coverage…</p>;
  const busy = !!job;
  return (
    <div className="space-y-6" data-testid="fopseo-translations">
      <div className="flex flex-wrap items-center gap-3 text-[11px]">
        <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-sm" style={{ background: "rgba(36,161,72,0.5)" }} /> Fully covered</span>
        <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-sm" style={{ background: "rgba(241,194,27,0.6)" }} /> Partial (some English)</span>
        <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-sm" style={{ background: "rgba(218,30,40,0.5)" }} /> Missing sections</span>
        <span className="flex items-center gap-1"><span className="inline-block h-3 w-3 rounded-sm" style={{ background: "rgba(120,120,120,0.3)" }} /> Out of scope</span>
        <span className="ms-auto opacity-55">Click any highlighted language to translate it. Grey = add a new language.</span>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-lg border border-[var(--primary)]/30 bg-[var(--primary)]/[0.05] p-3" data-testid="fill-everything-bar">
        <Sparkles size={16} className="text-[var(--primary)]" />
        <div className="text-sm font-medium">
          {totalGaps === 0 ? "Every manual is fully translated in all its languages." : `${totalGaps} language gap${totalGaps > 1 ? "s" : ""} across all manuals.`}
        </div>
        {bulk && (
          <div className="text-xs opacity-70" data-testid="fill-everything-progress">
            Overall: {bulk.done}/{bulk.total} language{bulk.total > 1 ? "s" : ""}{job ? ` · translating ${job.lang} (${job.done}/${job.total || "…"})` : ""}
          </div>
        )}
        <button onClick={fillEverything} disabled={busy || totalGaps === 0} data-testid="fill-everything-btn"
          className="ms-auto inline-flex items-center gap-1.5 rounded-md bg-[var(--primary)] px-4 py-2 text-sm font-semibold text-white lift disabled:cursor-not-allowed disabled:opacity-40">
          {bulk ? <RefreshCw size={15} className="animate-spin" /> : <Sparkles size={15} />}
          {bulk ? "Filling everything…" : "Fill everything"}
        </button>
      </div>

      {note && <p className="text-xs font-medium text-[var(--primary)]" data-testid="fill-note">{note}</p>}

      {data.bundles.map((b) => {
        const gapCount = b.coverage.filter((c) => c.code !== "ENG" && (c.missing > 0 || !c.in_scope)).length;
        return (
        <div key={b.id} className="rounded-lg border border-black/10 p-4 dark:border-white/10" data-testid={`cov-bundle-${b.id}`}>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <div className="text-sm font-semibold">{b.label}</div>
            <span className="rounded-full bg-black/5 px-2 py-0.5 text-[11px] opacity-70 dark:bg-white/10">{b.total_leaves} strings</span>
            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${b.complete_count === b.expected_count ? "bg-[#24a148]/15 text-[#24a148]" : "bg-[#f1c21b]/15 text-[#8a6d00]"}`} data-testid={`cov-badge-${b.id}`}>
              {b.complete_count === b.expected_count ? <CheckCircle2 size={12} /> : <AlertTriangle size={12} />}
              {b.complete_count}/{b.expected_count} languages complete
            </span>
            <button onClick={() => fillBundleGaps(b)} disabled={busy || gapCount === 0} data-testid={`cov-fill-gaps-${b.id}`}
              className="ms-auto inline-flex items-center gap-1.5 rounded-md bg-[var(--primary)] px-3 py-1.5 text-xs font-medium text-white lift disabled:cursor-not-allowed disabled:opacity-40">
              <Sparkles size={13} /> {gapCount === 0 ? "Fully translated" : `Fill ${gapCount} gap${gapCount > 1 ? "s" : ""}`}
            </button>
          </div>
          <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-5 lg:grid-cols-8" data-testid={`cov-grid-${b.id}`}>
            {b.coverage.map((c) => {
              const filling = job && job.bundleId === b.id && job.lang === c.code;
              const fillable = c.code !== "ENG" && (!c.in_scope || c.missing > 0 || c.untranslated > 0);
              return (
              <button key={c.code} type="button" disabled={busy || !fillable}
                onClick={() => fillLang(b.id, c.code)}
                title={c.in_scope
                  ? `${c.name}: ${c.translated} translated, ${c.untranslated} English, ${c.missing} missing${fillable ? " — click to fill" : ""}`
                  : `${c.name}: not in scope — click to translate & add this language`}
                className={`relative rounded-md border p-2 text-center transition ${c.in_scope ? "border-black/10 dark:border-white/10" : "border-dashed border-black/20 opacity-70 dark:border-white/20"} ${fillable && !busy ? "cursor-pointer hover:ring-2 hover:ring-[var(--primary)]/50" : "cursor-default"}`}
                style={{ background: covColor(c) }} data-testid={`cov-cell-${b.id}-${c.code}`}>
                <div className="text-[11px] font-semibold">{c.code}</div>
                <div className="text-lg font-semibold tabular-nums leading-tight">{c.pct}%</div>
                {c.in_scope && c.missing > 0 && <div className="text-[9px] text-[#da1e28]">{c.missing} missing</div>}
                {c.in_scope && c.missing === 0 && c.untranslated > 0 && <div className="text-[9px] text-[#8a6d00]">{c.untranslated} EN</div>}
                {!c.in_scope && <div className="text-[9px] opacity-60">+ add</div>}
                {filling && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center rounded-md bg-black/70 text-white" data-testid={`cov-filling-${b.id}-${c.code}`}>
                    <RefreshCw size={14} className="animate-spin" />
                    <div className="mt-1 text-[10px]">{job.total ? `${job.done}/${job.total}` : "starting…"}</div>
                  </div>
                )}
              </button>
              );
            })}
          </div>
        </div>
        );
      })}
      <p className="text-xs opacity-55">Coverage compares each language against the English source string-by-string. "%" is translation depth; a language is "complete" when it has no missing sections. Strings kept in English on purpose (brand and product names) count as English but are expected. "Fill gaps" translates only the missing/English strings with the cost-efficient model and never touches strings already translated.</p>
    </div>
  );
}



export default function FopSeo() {
  const [tab, setTab] = useState("traffic");
  const [nonce, setNonce] = useState(0);
  return (
    <div className="space-y-6" data-testid="fopseo-page">
      <div className="flex flex-wrap items-center gap-3">
        <LineChart size={22} className="text-[var(--primary)]" />
        <div>
          <h1 className="font-head text-2xl">FOP-SEO</h1>
          <p className="text-sm opacity-60">Anonymous, first-party site &amp; demo analytics (strictly-necessary, no marketing use).</p>
        </div>
        <button onClick={() => setNonce((n) => n + 1)} data-testid="fopseo-refresh"
          className="ms-auto inline-flex items-center gap-1.5 rounded-md border border-black/15 px-3 py-2 text-sm lift dark:border-white/15">
          <RefreshCw size={15} /> Refresh
        </button>
      </div>
      <div className="flex gap-2" data-testid="fopseo-tabs">
        {[["traffic", "Traffic"], ["demos", "Demos"], ["translations", "Translations"]].map(([k, lbl]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`fopseo-tab-${k}`}
            className={`rounded-full px-4 py-1.5 text-sm font-medium lift ${tab === k ? "bg-[var(--primary)] text-white" : "border border-black/15 dark:border-white/15 hover:bg-black/5"}`}>{lbl}</button>
        ))}
      </div>
      {tab === "traffic" && <TrafficTab key={`t-${nonce}`} />}
      {tab === "demos" && <DemosTab key={`d-${nonce}`} />}
      {tab === "translations" && <TranslationsTab key={`tr-${nonce}`} />}
    </div>
  );
}
